import type {AuthenticatedSession} from "./session";

export type LaunchRedemption =
    | {kind: "anonymous"; content: Blob; name: string}
    | {kind: "login"; session: AuthenticatedSession}
    | {kind: "project"; projectId: string; session: AuthenticatedSession}
    | {kind: "project-limit"; limit: number; session: AuthenticatedSession}
    | {kind: "starter-error"; session: AuthenticatedSession};

export class LaunchRedemptionError extends Error {}

const starterName = (header: string | null): string => {
    const encoded = /filename\*=UTF-8''([^;]+)/iu.exec(header ?? "")?.[1];
    if (!encoded) return "Projekt startowy";
    try {
        return decodeURIComponent(encoded).replace(/\.sb3$/iu, "") || "Projekt startowy";
    } catch {
        return "Projekt startowy";
    }
};

export const redeemLaunch = async (
    ticket: string,
    fetchImpl: typeof fetch = fetch,
): Promise<LaunchRedemption> => {
    const response = await fetchImpl("/api/launches/redeem", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({ticket}),
        credentials: "same-origin",
    });
    if (response.status === 409) {
        const failure = (await response.json()) as {
            error: string;
            limit?: number;
            session?: AuthenticatedSession;
        };
        if (failure.error === "PROJECT_LIMIT_REACHED" && failure.session && failure.limit) {
            return {kind: "project-limit", limit: failure.limit, session: failure.session};
        }
        throw new LaunchRedemptionError(failure.error);
    }
    if (response.status === 503) {
        const failure = (await response.json()) as {error: string; session?: AuthenticatedSession};
        if (failure.error === "STARTER_CREATE_FAILED" && failure.session) {
            return {kind: "starter-error", session: failure.session};
        }
        throw new LaunchRedemptionError(failure.error);
    }
    if (!response.ok) {
        const failure = await response.json().catch(() => ({error: "LAUNCH_FAILED"})) as {error?: string};
        throw new LaunchRedemptionError(failure.error ?? "LAUNCH_FAILED");
    }
    if (response.headers.get("Content-Type")?.startsWith("application/x.scratch.sb3")) {
        return {
            kind: "anonymous",
            content: await response.blob(),
            name: starterName(response.headers.get("Content-Disposition")),
        };
    }
    return response.json() as Promise<Extract<LaunchRedemption, {kind: "login" | "project"}>>;
};
